import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/ui/Header';
import MetricsOverview from './components/MetricsOverview';
import ReportFilters from './components/ReportFilters';
import Reports from './components/Reports';
import SurveyVolumeChart from './components/SurveyVolumeChart';
import PropertyTypeDistribution from './components/PropertyTypeDistribution';
import GeographicHeatMap from './components/GeographicHeatMap';
import ValuationAccuracyMetrics from './components/ValuationAccuracyMetrics';
import CustomReportBuilder from './components/CustomReportBuilder';
import ExportReportGenerator from '../../components/ui/ExportReportGenerator';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { API_ENDPOINTS } from '../../utils/config';

const ReportsAnalytics = () => {
  const [activeFilters, setActiveFilters] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedReportKey, setSelectedReportKey] = useState(null);
  const [activeView, setActiveView] = useState('reports'); // 'reports' or 'analytics'
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [geoData, setGeoData] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    if (activeView === 'analytics' && !analyticsData) {
      fetchAnalytics();
    }
  }, [activeView]);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const token = localStorage.getItem('token');
      const headers = { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const res = await axios.get(API_ENDPOINTS.ANALYTICS_DASHBOARD, { headers });
      setAnalyticsData(res.data);
      // fetch geographic data separately
      fetchGeoData(headers);
    } catch (err) {
      setAnalyticsError('Failed to load analytics data.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchGeoData = async (headers) => {
    setGeoLoading(true);
    try {
      const res = await axios.get(API_ENDPOINTS.ANALYTICS_GEOGRAPHIC, { headers });
      setGeoData(res.data);
    } catch (err) {
      // geo data failure is non-critical
    } finally {
      setGeoLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleReportSelect = (reportKey) => {
    setSelectedReportKey(reportKey);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="flex">
          {/* Sidebar - Filters */}
          <div className={`${sidebarCollapsed ? 'w-0' : 'w-80'} transition-all duration-300 overflow-hidden lg:block hidden`}>
            <div className="h-screen sticky top-16 overflow-y-auto border-r border-border bg-card">
              <div className="p-6">
                <ReportFilters 
                  onFiltersChange={handleFiltersChange}
                  reportKey={selectedReportKey}
                />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="p-6 space-y-8">
              {/* Page Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleSidebar}
                    className="lg:flex hidden"
                  >
                    <Icon name={sidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
                  </Button>
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">Reports & Analytics</h1>
                    <p className="text-muted-foreground mt-1">
                      Comprehensive business intelligence and performance insights
                    </p>
                  </div>
                </div>
                
                {/* View Toggle */}
                <div className="flex items-center space-x-2">
                  <Button
                    variant={activeView === 'reports' ? 'default' : 'outline'}
                    onClick={() => setActiveView('reports')}
                    size="sm"
                  >
                    Reports
                  </Button>
                  <Button
                    variant={activeView === 'analytics' ? 'default' : 'outline'}
                    onClick={() => setActiveView('analytics')}
                    size="sm"
                  >
                    Analytics
                  </Button>
                </div>
              </div>

              {/* Active Filters Display */}
              {Object.keys(activeFilters).length > 0 && (
                <div className="flex items-center space-x-2 p-4 bg-muted rounded-lg">
                  <Icon name="Filter" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-muted-foreground">Active filters:</span>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(activeFilters).map(([key, value]) => {
                      if (!value || value === '') return null;
                      return (
                        <span key={key} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                          {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Main Content Based on View */}
              {activeView === 'reports' && (
                <Reports
                  selectedReportKey={selectedReportKey}
                  onReportSelect={handleReportSelect}
                />
              )}

              {activeView === 'analytics' && analyticsLoading && (
                <div className="flex items-center justify-center py-24">
                  <div className="text-muted-foreground">Loading analytics...</div>
                </div>
              )}

              {activeView === 'analytics' && analyticsError && (
                <div className="p-6 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
                  <Icon name="AlertCircle" size={20} className="text-destructive" />
                  <span className="text-sm text-destructive">{analyticsError}</span>
                  <Button variant="outline" size="sm" onClick={fetchAnalytics} className="ml-auto">Retry</Button>
                </div>
              )}

              {activeView === 'analytics' && !analyticsLoading && !analyticsError && analyticsData && (
                <>
                  <MetricsOverview metrics={analyticsData.metrics} />
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <SurveyVolumeChart data={analyticsData.survey_volume} />
                    <PropertyTypeDistribution data={analyticsData.property_distribution} />
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <GeographicHeatMap data={geoData} isLoading={geoLoading} />
                    <ValuationAccuracyMetrics data={analyticsData.surveyor_performance} />
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <CustomReportBuilder />
                    <ExportReportGenerator />
                  </div>
                  <QuickActionToolbar />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsAnalytics;